<script>
import {
  GlAlert,
  GlCard,
  GlButton,
  GlButtonGroup,
  GlFormTextarea,
  GlModal,
  GlTooltipDirective,
  GlSkeletonLoader,
} from '@gitlab/ui';
import { __, s__ } from '~/locale';
import SolutionCard from 'ee/vue_shared/security_reports/components/solution_card_graphql.vue';
import IssueNote from 'ee/vue_shared/security_reports/components/issue_note_graphql.vue';
import MergeRequestNote from 'ee/vue_shared/security_reports/components/merge_request_note_graphql.vue';
import VulnerabilityDetailsGraphql from 'ee/security_dashboard/components/shared/vulnerability_details_graphql/index.vue';
import EventItem from 'ee/vue_shared/security_reports/components/event_item.vue';
import securityReportFindingQuery from 'ee/security_dashboard/graphql/queries/security_report_finding.query.graphql';
import dismissFindingMutation from 'ee/security_dashboard/graphql/mutations/dismiss_finding.mutation.graphql';
import revertFindingToDetectedMutation from 'ee/security_dashboard/graphql/mutations/revert_finding_to_detected.mutation.graphql';
import { updateFindingState } from 'ee/security_dashboard/graphql/cache_updates';

export const STATE_DISMISSED = 'DISMISSED';
export const STATE_DETECTED = 'DETECTED';

export default {
  components: {
    EventItem,
    GlAlert,
    GlCard,
    GlButton,
    GlButtonGroup,
    GlFormTextarea,
    GlModal,
    GlSkeletonLoader,
    IssueNote,
    MergeRequestNote,
    SolutionCard,
    VulnerabilityDetailsGraphql,
  },
  directives: {
    GlTooltip: GlTooltipDirective,
  },
  props: {
    findingUuid: {
      type: String,
      required: true,
    },
    pipelineIid: {
      type: Number,
      required: true,
    },
    projectFullPath: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      hasFindingFetchError: false,
      errorMessage: '',
      isUpdatingFindingState: false,
      isShowingDismissalCommentSection: false,
      dismissalComment: '',
    };
  },
  apollo: {
    finding: {
      query: securityReportFindingQuery,
      variables() {
        return this.queryVariables;
      },
      update(data) {
        return data.project?.pipeline?.securityReportFinding;
      },
      error() {
        this.finding = null;
        this.errorMessage = this.$options.i18n.fetchErrorMessage;
      },
    },
  },
  computed: {
    queryVariables() {
      const { projectFullPath, pipelineIid, findingUuid } = this;

      return {
        projectFullPath,
        pipelineIid,
        findingUuid,
      };
    },
    remediation() {
      return this.finding.remediations?.[0];
    },
    issueLinks() {
      return this.finding.issueLinks?.nodes || [];
    },
    canDownloadPatchForThisVulnerability() {
      return !this.finding.hasMergeRequest && this.remediation?.diff?.length > 0;
    },
    showErrorMessage() {
      return !this.isLoading && (Boolean(this.errorMessage) || !this.finding);
    },
    isLoading() {
      return this.$apollo.queries.finding.loading;
    },
    isFindingDismissed() {
      return this.finding?.state === STATE_DISMISSED;
    },
    toggledFindingStateMutationConfig() {
      const { i18n } = this.$options;
      const toggledState = this.isFindingDismissed ? STATE_DETECTED : STATE_DISMISSED;
      const configs = {
        [STATE_DISMISSED]: {
          mutationName: 'securityFindingDismiss',
          mutation: dismissFindingMutation,
          errorMessage: i18n.dismissErrorMessage,
        },
        [STATE_DETECTED]: {
          mutationName: 'securityFindingRevertToDetected',
          mutation: revertFindingToDetectedMutation,
          errorMessage: i18n.revertDismissErrorMessage,
        },
      };

      return { ...configs[toggledState], toggledState };
    },
    currentUser() {
      const {
        current_user_id: id,
        current_username: username,
        current_user_fullname: name,
      } = window.gon;

      return {
        author: {
          id,
          name,
          username,
          state: 'active',
        },
      };
    },
    dismissButtonText() {
      const {
        isFindingDismissed,
        isShowingDismissalCommentSection,
        $options: { i18n },
      } = this;

      if (isFindingDismissed) {
        return i18n.revertDismissFinding;
      }

      if (isShowingDismissalCommentSection) {
        return i18n.addCommentAndDismiss;
      }

      return i18n.dismissFinding;
    },
  },
  methods: {
    handleCancel() {
      if (this.isShowingDismissalCommentSection) {
        this.isShowingDismissalCommentSection = false;
      } else {
        this.closeModal();
      }
    },
    closeModal() {
      this.$refs.modal.hide();
    },
    async toggleFindingState() {
      this.isUpdatingFindingState = true;

      const {
        mutation,
        errorMessage,
        mutationName,
        toggledState,
      } = this.toggledFindingStateMutationConfig;

      try {
        await this.$apollo.mutate({
          mutation,
          variables: {
            uuid: this.findingUuid,
            ...(this.dismissalComment && { comment: this.dismissalComment }),
          },
          update: (store, response) => {
            const { errors } = response.data[mutationName];

            if (errors.length > 0) {
              throw new Error(errors[0]);
            }

            updateFindingState({
              state: toggledState,
              store,
              query: securityReportFindingQuery,
              variables: this.queryVariables,
            });

            this.$emit('state-update', toggledState);
            this.closeModal();
          },
        });
      } catch {
        this.errorMessage = errorMessage;
      } finally {
        this.isUpdatingFindingState = false;
      }
    },
  },
  i18n: {
    fetchErrorTitle: __('Error'),
    fetchErrorMessage: s__(
      'SecurityReports|There was an error fetching the finding. Please try again.',
    ),
    dismissErrorMessage: s__(
      'SecurityReports|There was an error dismissing the finding. Please try again.',
    ),
    revertDismissErrorMessage: s__('SecurityReports|There was an error reverting the dismissal.'),
    cancel: __('Cancel'),
    dismissFinding: s__('SecurityReports|Dismiss vulnerability'),
    revertDismissFinding: s__('SecurityReports|Undo dismiss'),
    addCommentAndDismiss: s__('SecurityReports|Add comment and dismiss'),
  },
};
</script>

<template>
  <gl-modal
    ref="modal"
    size="lg"
    :visible="true"
    modal-id="security-finding-details"
    @hidden="$emit('hide')"
  >
    <template #modal-header>
      <gl-skeleton-loader
        v-if="isLoading"
        data-testid="title-loading-indicator"
        :width="400"
        :lines="1"
      />
      <h2 v-else class="gl-m-0 gl-font-lg">
        {{ finding ? finding.title : $options.i18n.fetchErrorTitle }}
      </h2>
    </template>

    <div v-if="isLoading" data-testid="content-loading-indicator">
      <!-- this is just a placeholder until the designs have been finalized. Captured in: https://gitlab.com/gitlab-org/gitlab/-/issues/377217 -->
      <!-- the skeleton loaders will also be moved into a separate component so they can be re-used -->
      <gl-skeleton-loader :width="80" :lines="1" />
      <gl-skeleton-loader :width="200" :lines="1" />
    </div>

    <template v-else-if="finding">
      <vulnerability-details-graphql v-bind="finding" />

      <solution-card
        :solution="finding.solution"
        :remediation="remediation"
        :merge-request="finding.mergeRequest"
      />

      <issue-note :issue-links="issueLinks" :project="finding.project" />
      <merge-request-note :merge-request="finding.mergeRequest" :project="finding.project" />

      <!-- adding the comment-history is captured in: https://gitlab.com/gitlab-org/gitlab/-/issues/337488 -->
      <gl-card v-if="isShowingDismissalCommentSection" data-testid="dismissal-comment-section">
        <event-item
          :author="currentUser.author"
          icon-name="cancel"
          icon-class="ci-status-icon-pending"
        />
        <div class="gl-mt-5 gl-pt-5 gl-border-t">
          <gl-form-textarea
            v-model="dismissalComment"
            autofocus
            @keydown.meta.enter="toggleFindingState"
          />
        </div>
      </gl-card>
    </template>

    <gl-alert v-if="showErrorMessage" variant="danger" :dismissible="false">
      {{ errorMessage || $options.i18n.fetchErrorMessage }}
    </gl-alert>

    <template #modal-footer>
      <div data-testid="footer">
        <gl-button data-testid="cancel-button" @click="handleCancel">
          {{ $options.i18n.cancel }}
        </gl-button>
        <gl-button-group>
          <gl-button
            data-testid="dismiss-button"
            :loading="isUpdatingFindingState"
            @click="toggleFindingState"
          >
            {{ dismissButtonText }}
          </gl-button>
          <gl-button
            v-if="!isFindingDismissed && !isShowingDismissalCommentSection"
            v-gl-tooltip
            data-testid="dismiss-with-comment-button"
            icon="comment"
            :title="$options.i18n.addCommentAndDismiss"
            @click="isShowingDismissalCommentSection = true"
          />
        </gl-button-group>
      </div>
    </template>
  </gl-modal>
</template>
